import React, { useContext, useState } from 'react';
import {
  Button,
  Hamburger,
  makeStyles,
  Menu,
  MenuGroupHeader,
  MenuItemLink,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Nav,
  NavCategory,
  NavCategoryItem,
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  NavItem,
  NavSubItem,
  NavSubItemGroup,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { INavLink, INavLinkGroup } from '@m365-admin/nav';
import {
  BuildingRegular,
  DocumentRegular,
  DocumentTextRegular,
  GlobeRegular,
  GridRegular,
  HomeRegular,
  NavigationRegular,
  TimelineRegular,
  WrenchRegular,
} from '@fluentui/react-icons';
import { useLocation, useHistory } from 'react-router-dom';
import navLinkGroups from './Navigation.config';
import {
  mobileNavTriggerWrapper,
  mobileNavTrigger,
  defaultNavWrapper,
  leftNavContainer,
  mobileNavWrapper,
} from '../../app/common/styles/CommonStyleObjects';
import {
  MOBILE_NAV_MAX_WIDTH,
  MOBILE_NAV_MAX_WIDTH_BREAKPOINT,
  NAV_HEIGHT,
} from '../../app/common/constants/SiteConstants';
import OutsideAlerter from '../../utils/OutsideAlerter';
import { getLinkValue, getSelectedNavValues } from '../../utils/navUtils';
import { StatUser } from '../../utils/statApi/UsersApi';
import UserContext from '../../utils/authorization/UserContext';

/** Maps v8 Fluent UI icon names (used in Navigation.config.ts) to v9 react-icons components. */
const ICON_SIZE = 22;

const iconMap: Record<string, JSX.Element> = {
  Home: <HomeRegular fontSize={ICON_SIZE} />,
  CityNext: <BuildingRegular fontSize={ICON_SIZE} />,
  ReportDocument: <DocumentTextRegular fontSize={ICON_SIZE} />,
  TimelineProgress: <TimelineRegular fontSize={ICON_SIZE} />,
  ThumbnailView: <GridRegular fontSize={ICON_SIZE} />,
  Globe: <GlobeRegular fontSize={ICON_SIZE} />,
  Documentation: <DocumentRegular fontSize={ICON_SIZE} />,
  DeveloperTools: <WrenchRegular fontSize={ICON_SIZE} />,
};

interface INavigationProps {
  navIsHidden: boolean;
  setNavIsHidden: (navIsHidden: boolean) => void;
}

const NAV_WIDTH = 280;
const COLLAPSED_NAV_WIDTH = 48;

const useStyles = makeStyles({
  // -- Fixed sidebar container: animates width on collapse/expand.
  //    NavDrawer keeps its default 260px width inside; overflow:hidden clips to the
  //    container width so only icons are visible when collapsed to 48px. --
  navSticky: {
    position: 'fixed',
    height: NAV_HEIGHT,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground4,
    transitionProperty: 'width, min-width',
    transitionDuration: '0.3s',
    transitionTimingFunction: 'ease',
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      marginTop: '48px',
    },
  },
  navStickyOpen: {
    width: `${NAV_WIDTH}px`,
    minWidth: `${NAV_WIDTH}px`,
    whiteSpace: 'nowrap',
    ':hover': { overflowY: 'auto' },
  },
  navStickyCollapsed: {
    width: `${COLLAPSED_NAV_WIDTH}px`,
    minWidth: `${COLLAPSED_NAV_WIDTH}px`,
  },
  // -- Override NavDrawer's default height:auto to fill the fixed sidebar --
  navDrawer: {
    height: '100%',
    width: '100%',
  },
  navDrawerCollapsed: {
    // Constrain NavDrawer to collapsed width so tooltip triggers report correct
    // bounding rects and positioning="after" places tooltips at the sidebar edge.
    width: `${COLLAPSED_NAV_WIDTH}px`,
    minWidth: `${COLLAPSED_NAV_WIDTH}px`,
    maxWidth: `${COLLAPSED_NAV_WIDTH}px`,
    // Prevent long item names (e.g. "Dividend Reviews") from wrapping and
    // creating extra vertical gaps between icons in the collapsed state.
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
  },
  /** Applied directly to NavDrawerBody when collapsed to remove padding. */
  navDrawerBodyCollapsed: {
    paddingLeft: '0',
    paddingRight: '0',
    overflowX: 'hidden',
  },
  /** Hide scrollbar by default, show on hover for clean UX. */
  navDrawerBody: {
    overflowY: 'hidden',
    ':hover': { overflowY: 'auto' },
  },
  /** Override NavSubItemGroup's overflow:hidden which clips the selected indicator. */
  navSubItemGroupVisible: {
    overflow: 'visible',
  },
  navDrawerHeader: {
    paddingInlineStart: '10px',
  },
  /** Blue bar shown on the selected item/category in collapsed nav mode. */
  selectedIndicator: {
    position: 'absolute',
    left: '0',
    top: '25%',
    bottom: '25%',
    width: '3px',
    borderRadius: '9999px',
    backgroundColor: tokens.colorCompoundBrandForeground1,
    zIndex: 1,
    pointerEvents: 'none',
  },
  /** Wrapper for items that need relative positioning for the selectedIndicator. */
  positionRelative: {
    position: 'relative',
  },
});

interface ICollapsedNavFlyoutProps {
  link: INavLink;
  value: string;
  isSelected: boolean;
  styles: ReturnType<typeof useStyles>;
}

/**
 * Collapsed icon-rail category rendered as a Fluent UI v9 `Menu` flyout.
 *
 * Fluent's `Menu` provides — maintained and tested upstream — roving arrow-key
 * focus, Home/End, typeahead, Escape-to-close with focus return to the trigger,
 * outside-click dismiss, self-positioning, and automatic `aria-haspopup` /
 * `aria-expanded` wiring. `openOnHover` preserves the original mouse behavior.
 * This removes the hand-rolled focus-management refs, `setTimeout` open-focus
 * hack, manual Arrow/Escape key handling, and `getBoundingClientRect`
 * positioning that the previous implementation required.
 */
const CollapsedNavFlyout = ({
  link,
  value,
  isSelected,
  styles,
}: ICollapsedNavFlyoutProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const categoryName = typeof link.name === 'string' ? link.name : '';
  // `MenuTrigger` wires aria-haspopup/aria-expanded automatically; aria-controls
  // is added only while open so it never references an unmounted popover.
  const flyoutId = `flyout-${value}`;

  return (
    <div className={styles.positionRelative}>
      {isSelected && <div className={styles.selectedIndicator} />}
      <NavCategory value={value}>
        <Menu
          open={open}
          onOpenChange={(_, data) => setOpen(data.open)}
          openOnHover
          positioning="after-top"
        >
          <MenuTrigger disableButtonEnhancement>
            <NavCategoryItem
              icon={link.icon ? iconMap[link.icon] : undefined}
              aria-label={categoryName}
              aria-controls={open ? flyoutId : undefined}
            />
          </MenuTrigger>
          <MenuPopover id={flyoutId}>
            <MenuList>
              <MenuGroupHeader>{link.name}</MenuGroupHeader>
              {link.links?.map((childLink, childIndex) => {
                const childKey =
                  childLink.key ||
                  childLink.url ||
                  childLink.href ||
                  `${value}-child-${childIndex}`;
                return (
                  <MenuItemLink
                    key={childKey}
                    href={childLink.href || childLink.url}
                    onClick={(ev: React.MouseEvent<HTMLElement>) => {
                      childLink.onClick?.(ev as React.MouseEvent<HTMLElement>, childLink);
                    }}
                  >
                    {childLink.name}
                  </MenuItemLink>
                );
              })}
            </MenuList>
          </MenuPopover>
        </Menu>
      </NavCategory>
    </div>
  );
};

const Navigation = ({ navIsHidden, setNavIsHidden }: INavigationProps): JSX.Element => {
  const history = useHistory();
  const isOpen = !navIsHidden;
  const styles = useStyles();

  const navStickyClassName = mergeClasses(
    styles.navSticky,
    isOpen ? styles.navStickyOpen : styles.navStickyCollapsed,
  );

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const user = useContext(UserContext) as unknown as StatUser;
  const location = useLocation();

  const closeMenuOnMobileOutsideClick = (): void => {
    const windowWidth = window.innerWidth;
    if (windowWidth < MOBILE_NAV_MAX_WIDTH) {
      setNavIsHidden(true);
    }
  };

  const handleLinkClick = (ev: React.MouseEvent<HTMLElement>, item: INavLink): void => {
    ev.preventDefault();
    ev.stopPropagation();
    history.push(item.url || item.href);
  };

  const groups = navLinkGroups(handleLinkClick, location.pathname, user);

  const { selectedValue, selectedCategoryValue } = getSelectedNavValues(groups);

  const renderLink = (link: INavLink, index: number, groupKey: string): JSX.Element => {
    const value = getLinkValue(link, index, groupKey);
    const href = link.href || link.url || '';
    const currentPath = location.pathname;
    const normalizedPath = currentPath.replace(/^\/+|\/+$/g, '');
    const normalizedHref = href.replace(/^\/+|\/+$/g, '');
    const isExactMatch = normalizedPath === normalizedHref;
    const isAncestorMatch =
      !isExactMatch && normalizedHref !== '' && normalizedPath.startsWith(`${normalizedHref}/`);
    let ariaCurrent: 'page' | 'true' | undefined;
    if (isExactMatch) {
      ariaCurrent = 'page';
    } else if (isAncestorMatch) {
      ariaCurrent = 'true';
    }

    if (link.links?.length) {
      // Collapsed icon rail: render the category as a Fluent v9 Menu flyout
      // (keyboard + ARIA handled by Fluent). Expanded rail: inline sub-items.
      if (!isOpen) {
        return (
          <CollapsedNavFlyout
            key={value}
            link={link}
            value={value}
            isSelected={selectedCategoryValue === value}
            styles={styles}
          />
        );
      }

      return (
        <NavCategory key={value} value={value}>
          <NavCategoryItem icon={link.icon ? iconMap[link.icon] : undefined}>
            {link.name}
          </NavCategoryItem>
          <NavSubItemGroup className={styles.navSubItemGroupVisible}>
            <div role="list">
              {link.links.map((childLink, childIndex) => {
                const childValue =
                  childLink.key ||
                  childLink.url ||
                  childLink.href ||
                  `${value}-child-${childIndex}`;

                return (
                  <div role="listitem" key={childValue}>
                    <NavSubItem
                      value={childValue}
                      href={childLink.href || childLink.url}
                      aria-label={typeof childLink.name === 'string' ? childLink.name : undefined}
                      onClick={(ev: React.MouseEvent<HTMLElement>) => {
                        childLink.onClick?.(ev as React.MouseEvent<HTMLElement>, childLink);
                      }}
                    >
                      {childLink.name}
                    </NavSubItem>
                  </div>
                );
              })}
            </div>
          </NavSubItemGroup>
        </NavCategory>
      );
    }

    if (!isOpen) {
      const tooltipText = typeof link.name === 'string' ? link.name : '';
      const isItemSelected = selectedValue === value;
      return (
        <div className={styles.positionRelative}>
          {isItemSelected && <div className={styles.selectedIndicator} />}
          <Tooltip
            key={value}
            content={tooltipText}
            relationship="label"
            positioning="after"
            withArrow
          >
            <NavItem
              value={value}
              href={link.href || link.url}
              aria-current={ariaCurrent}
              icon={link.icon ? iconMap[link.icon] : undefined}
              aria-label={typeof link.name === 'string' ? link.name : undefined}
              onClick={(ev: React.MouseEvent<HTMLElement>) => {
                link.onClick?.(ev as React.MouseEvent<HTMLElement>, link);
              }}
            >
              {/* icon only */}
            </NavItem>
          </Tooltip>
        </div>
      );
    }

    return (
      <NavItem
        key={value}
        value={value}
        href={link.href || link.url}
        aria-current={ariaCurrent}
        icon={link.icon ? iconMap[link.icon] : undefined}
        aria-label={typeof link.name === 'string' ? link.name : undefined}
        onClick={(ev: React.MouseEvent<HTMLElement>) => {
          link.onClick?.(ev as React.MouseEvent<HTMLElement>, link);
        }}
      >
        {link.name}
      </NavItem>
    );
  };

  return (
    <div className={leftNavContainer}>
      <div className={mobileNavTriggerWrapper}>
        <Button
          appearance="subtle"
          aria-label="Open mobile navigation"
          icon={<NavigationRegular />}
          className={mobileNavTrigger}
          onClick={(): void => setNavIsHidden(false)}
        />
      </div>

      <OutsideAlerter onClickOutside={(): void => closeMenuOnMobileOutsideClick()}>
        <div className={navIsHidden ? defaultNavWrapper : mobileNavWrapper}>
          <div className={navStickyClassName}>
            <NavDrawer
              type="inline"
              open
              aria-label="Primary navigation"
              selectedValue={selectedValue}
              selectedCategoryValue={selectedCategoryValue}
              defaultOpenCategories={selectedCategoryValue ? [selectedCategoryValue] : undefined}
              className={mergeClasses(styles.navDrawer, !isOpen && styles.navDrawerCollapsed)}
            >
              <NavDrawerHeader className={styles.navDrawerHeader}>
                <Tooltip
                  content={isOpen ? 'Collapse navigation' : 'Expand navigation'}
                  relationship="label"
                >
                  <Hamburger
                    aria-label={isOpen ? 'Collapse navigation' : 'Expand navigation'}
                    onClick={() => setNavIsHidden(isOpen)}
                  />
                </Tooltip>
              </NavDrawerHeader>

              <NavDrawerBody
                className={mergeClasses(
                  styles.navDrawerBody,
                  !isOpen && styles.navDrawerBodyCollapsed,
                )}
              >
                <Nav
                  aria-label="Main navigation"
                  key={isOpen ? 'expanded' : 'collapsed'}
                  selectedValue={isOpen ? selectedValue : selectedCategoryValue ?? selectedValue}
                  selectedCategoryValue={selectedCategoryValue}
                  defaultOpenCategories={
                    isOpen && selectedCategoryValue ? [selectedCategoryValue] : []
                  }
                >
                  <div role="list">
                    {groups.flatMap((group: INavLinkGroup) =>
                      group.links.map((link, linkIndex) => {
                        const itemKey = getLinkValue(link, linkIndex, group.key);
                        return (
                          <div role="listitem" key={itemKey}>
                            {renderLink(link, linkIndex, group.key)}
                          </div>
                        );
                      }),
                    )}
                  </div>
                </Nav>
              </NavDrawerBody>
            </NavDrawer>
          </div>
        </div>
      </OutsideAlerter>
    </div>
  );
};

export default Navigation;
